import type { 
  ApplicationInfo, Customer, Vehicle, 
  CreditCheck, LenderQuote, 
  PayoutPackage,
  HpPrice,
  PcpPrice,
  Company,
  HpAprRangesV4
} from "./";
import { Affordability } from "./";
import { ProductType_Enum, ActionType_Enum, ApplicationType_Enum, VehicleType_Enum, LoanStatusType_Enum } from "../enums";
import { ApplicationProperties } from "../status";
import useMapProposals from "src/functions/mappers/use-map-proposal";
import applicationStatus from "src/functions/account/application-status";
import { sanitiseVehicleFailReason } from "src/functions";
import { useProposalStore } from "src/stores/proposal-store";
import { calculate, MonthlyPrice, Vehicle as PricingVehicle, VehicleType, Customer as PricingCustomer, DrivingLicence, Dealer, Proposal, AffiliateType, Address, Employment, DealerType } from '@cf247/pricing';
import { QuickQuote } from "../quote";
import { useAuthStore } from "src/stores/auth-store";
import { mockAlpheraProposal, mockCaProposal, mockCloseProposal, mockMotoNovoProposal, mockNorthridgeProposal, mockVwfsProposal, mockZopaProposal } from "@cf247/pricing/dist/mocks";

interface ILoanApplication{
  loanAmount: number | null;
  affiliateUserId: string;
  dealerId: number | null;
  dealerReference: number | null;
  introducerReference: string | null;
  introducerName: string | null;
  applicationType: ApplicationType_Enum
  customer: Customer | null;
  affordability: Affordability | null;
  vehicle: Vehicle | null;
  pxVehicle: Vehicle | null;
  deposit: number | null;
  pxValue: number | null;
  productType: ProductType_Enum;
  futuresMileage: number | null;
  settlement: number | null;
  settlementFinanceCompany: string | null;
  pxReg: string | null;
  pxMileage: number | null;
  pxMake: string | null;
  pxModel: string | null;
  term: number | null;
  created: Date;
  createdBy: string;
  lastUpdated: Date;
  updatedBy: string;
  cosmosId: string;
  submissionId: string;
  loanVehicleId: number | null;
  pxLoanVehicleId: number | null;
  brokerReference: number;
  state: number;
  decisionStatus: number;
  accountManager: number | null;
  proposals: Proposal[];
  creditCheck: CreditCheck | null;
  loadingCreditCheck: boolean;
  isQuickQuote: boolean;
  quickQuoteId: string | null;
  quickQuote: QuickQuote | null;
  creditProfile: number | null;
  lastSocketUpdate: number | null;
  firstContactSource: number | null;
  lenderQuote: LenderQuote | null;
  hasDealerAgreedTermsAndConditions: boolean | null;
  dealerAgreedTermsAndConditionsDate: Date | null;
  isV2App: boolean;
  isVehicleBusinessUse: boolean | null;
  actionType: ActionType_Enum | null;
  applicationInfo: ApplicationInfo | null;
  payoutPack: PayoutPackage | null;
  isNtuFlagged?: boolean | null;
  companyDetails?: Company | null;
}
class LoanApplication implements ILoanApplication{
  loanAmount: number | null;
  affiliateUserId: string;
  dealerId: number | null;
  dealerReference: number | null;
  introducerReference: string | null;
  introducerName: string | null;
  applicationType: ApplicationType_Enum;
  customer: Customer | null;
  affordability: Affordability | null;
  vehicle: Vehicle | null;
  pxVehicle: Vehicle | null;
  deposit: number | null;
  pxValue: number | null;
  productType: ProductType_Enum;
  futuresMileage: number | null;
  settlement: number | null;
  settlementFinanceCompany: string | null;
  pxReg: string | null;
  pxMileage: number | null;
  pxMake: string | null;
  pxModel: string | null;
  term: number | null;
  created: Date;
  createdBy: string;
  lastUpdated: Date;
  updatedBy: string;
  cosmosId: string;
  submissionId: string;
  loanVehicleId: number | null;
  pxLoanVehicleId: number | null;
  brokerReference: number;
  state: LoanStatusType_Enum;
  decisionStatus: number;
  accountManager: number | null;
  proposals: Proposal[];
  creditCheck: CreditCheck | null;
  loadingCreditCheck: boolean;
  isQuickQuote: boolean;
  quickQuoteId: string | null;
  quickQuote: QuickQuote | null;
  creditProfile: number | null;
  lastSocketUpdate: number | null;
  firstContactSource: number | null;
  lenderQuote: LenderQuote | null;
  hasDealerAgreedTermsAndConditions: boolean | null;
  dealerAgreedTermsAndConditionsDate: Date | null;
  isV2App: boolean;
  isVehicleBusinessUse: boolean | null;
  actionType: ActionType_Enum | null;
  applicationInfo: ApplicationInfo | null;
  payoutPack: PayoutPackage | null;
  betterRateAvailable?: boolean | null;
  bestRatePrice?: HpPrice | PcpPrice | null;
  isNtuFlagged?: boolean | null;
  companyDetails?: Company | null;

  constructor();
  constructor(loanApplication?: ILoanApplication);
  constructor(
    loanApplication?: ILoanApplication,
  ) {
    this.loanAmount = loanApplication?.loanAmount || null;
    this.affiliateUserId = loanApplication?.affiliateUserId || "";
    this.dealerId = loanApplication?.dealerId || null;
    
    this.dealerReference = loanApplication?.dealerReference || null;
    this.introducerReference = loanApplication?.introducerReference || null;
    this.introducerName = loanApplication?.introducerName || null;
    this.applicationType = loanApplication?.applicationType || ApplicationType_Enum.Eligibility;
    this.customer = loanApplication?.customer || null;
    this.affordability = loanApplication?.affordability || null;
    this.vehicle = loanApplication?.vehicle || null;
    this.pxVehicle = loanApplication?.pxVehicle || null;
    this.deposit = loanApplication?.deposit || null;
    this.pxValue = loanApplication?.pxValue || null;
    this.productType = loanApplication?.productType || ProductType_Enum.HP;
    this.futuresMileage = loanApplication?.futuresMileage || null;
    this.settlement = loanApplication?.settlement || null;
    this.settlementFinanceCompany = loanApplication?.settlementFinanceCompany || null;
    this.pxReg = loanApplication?.pxReg || null;
    this.pxMileage = loanApplication?.pxMileage || null;
    this.pxMake = loanApplication?.pxMake || null;
    this.pxModel = loanApplication?.pxModel || null;
    this.term = loanApplication?.term || null;
    this.created = loanApplication?.created || new Date();
    this.createdBy = loanApplication?.createdBy || "";
    this.lastUpdated = loanApplication?.lastUpdated || new Date();
    this.updatedBy = loanApplication?.updatedBy || "";
    this.cosmosId = loanApplication?.cosmosId || "";
    this.submissionId = loanApplication?.submissionId || "";
    this.loanVehicleId = loanApplication?.loanVehicleId || null;
    this.pxLoanVehicleId = loanApplication?.pxLoanVehicleId || null;
    this.brokerReference = loanApplication?.brokerReference || 0;
    this.state = loanApplication?.state || 0;
    this.decisionStatus = loanApplication?.decisionStatus || 0;
    this.accountManager = loanApplication?.accountManager || null;
    this.proposals = loanApplication?.proposals ? useMapProposals(loanApplication.proposals, loanApplication?.dealerId ?? 0) : [];
    this.creditCheck = loanApplication?.creditCheck || null;
    this.loadingCreditCheck = false;
    this.isQuickQuote = loanApplication?.isQuickQuote || false;
    this.quickQuoteId = loanApplication?.quickQuoteId || null;
    this.quickQuote = loanApplication?.quickQuote || null;
    this.creditProfile = loanApplication?.creditProfile || null;
    this.lastSocketUpdate = null;
    this.firstContactSource = null;
    this.lenderQuote = loanApplication?.lenderQuote || null;
    this.hasDealerAgreedTermsAndConditions = loanApplication?.hasDealerAgreedTermsAndConditions || null;
    this.dealerAgreedTermsAndConditionsDate = loanApplication?.dealerAgreedTermsAndConditionsDate || null;
    this.isV2App = loanApplication?.isV2App || false;
    this.isVehicleBusinessUse = loanApplication?.isVehicleBusinessUse || null;
    this.actionType = loanApplication?.actionType || null;
    this.applicationInfo = loanApplication?.applicationInfo || null;
    this.payoutPack = loanApplication?.payoutPack || null;
    this.isNtuFlagged = loanApplication?.isNtuFlagged || null;
    this.companyDetails = loanApplication?.companyDetails || null;
    
    if(this.vehicle && !this.vehicle.vrm){
      //@ts-ignore - Hack as we changed the property name, so ensure old apps use new property
      this.vehicle.vrm = this.vehicle.vehicleRegistration;
    }

    if(!this.affordability){
      this.affordability = new Affordability();
    }
  }

  public get initialisingQuotes() {
    return useProposalStore().initialisingQuotes;
  }
  
  public get properties(): ApplicationProperties {
    let as = applicationStatus(this);
    return as;
  }

  async priceVehicle(): Promise<MonthlyPrice[] | null>{

    if(!this.vehicle){
      return null;
    }

    if(this.isQuickQuote){

      const props = [
        mockAlpheraProposal(),
        mockCaProposal(),
        mockCloseProposal(),
        mockMotoNovoProposal(),
        mockNorthridgeProposal(),
        mockVwfsProposal(),
        mockZopaProposal()
      ]

      this.proposals = props;
    }

    if(this.proposals.length == 0){
      return null;
    }

    if(!this.vehicle.vehicleType){
      this.vehicle.vehicleType = VehicleType_Enum.Car;
    }
          
    const pricingVehicle = {
      price: this.vehicle.price,
      mileage: this.vehicle.mileage,
      make: this.vehicle.make,
      model: this.vehicle.model,
      vrm: this.vehicle.vrm,
      registrationDate: this.vehicle.registrationDate ? new Date(this.vehicle.registrationDate) : this.vehicle.vehicleDateOfFirstReg ? new Date(this.vehicle.vehicleDateOfFirstReg) : null,
      vehicleType: this.vehicle.vehicleType as unknown as VehicleType,
    } as PricingVehicle;

    const pricingCustomer = {
      firstName: this.customer?.firstName ?? "",
      surname: this.customer?.lastName ?? "",
      dateOfBirth: this.customer?.dateOfBirth ? new Date(this.customer.dateOfBirth) : null,
      drivingLicence: this.customer?.drivingLicenceType ? this.customer.drivingLicenceType as unknown as DrivingLicence : null,
      netMonthlyIncome: this.customer?.netMonthlyIncome ?? null,
      addressHistory: this.customer?.residentialAddressHistory.map((a, i) => {
        return {
          index: i,
          postcode: a.postcode,
          yearsAt: a.yearsAtAddress,
          monthsAt: a.monthsAtAddress,
          street: a.street,
          town: a.town,
          status: a.residentialStatusType as unknown as number,
        } as Address
      }) ?? [],
      employmentHistory: this.customer?.employmentHistory.map((e, i) => {
        return {
          index: i,
          status: e.employmentStatus as unknown as number,
          yearsAt: e.yearsAtEmployer,
          monthsAt: e.monthsAtEmployer,
          occupation: e.occupation,
          employer: e.employerName
        } as Employment;
      }) ?? [],
      creditCheckScore: this.isQuickQuote ? 600 : this.creditCheck?.score ?? 0
    } as PricingCustomer;

    const d = useAuthStore().dealers?.find(d => d.affiliateUserId === Number(this.affiliateUserId));
    let td = 0;
    if(!d){
      td = Number(useAuthStore().affiliateUserId);
    }
    const pricingDealer = {
      id: d ? d.dealerId : useAuthStore().dealerId ?? null,
      adminFee: {
        amount: d && d.adminFee? d.adminFee : useAuthStore().adminFee ?? 0,
        includedInFinance: true
      },
      type: DealerType.NonFranchisedDealer
    } as Dealer;

    const prices = await calculate(
        [pricingVehicle],
        this.proposals as unknown as Proposal[],
        pricingCustomer,
        pricingDealer,
        this.term ?? 60,
        this.deposit ?? 0,
        this.pxValue ?? 0,
        this.settlement ?? 0,
        this.vehicle.annualMileage ?? 8000,
        null,
        {
          affiliateId: d?.affiliateUserId ?? td,
          affiliateType: AffiliateType.Dealer
        }
    )

    this.vehicle.monthlyPrices = prices;

    return prices;
  }
  
  public get vehicleValid() {

    let lenders = [] as { financeCompanyId: number, valid: boolean, accepted?: boolean, reason: string | null, failReasons?: { type: string; message: string }[] }[];

    const hasValidPrice = this.vehicle && this.vehicle.monthlyPrices && this.vehicle.monthlyPrices.length > 0 && this.vehicle.monthlyPrices.some(mp => !mp.failed);
    if(this.proposals && hasValidPrice){

      lenders = this.proposals.map(p => {

        if(this.properties.hasDealSheet){
          if(this.payoutPack && this.payoutPack.dealSheet && this.payoutPack.dealSheet.vehicleRegistration === this.vehicle!.vrm && this.payoutPack.dealSheet.financeCompanyId === p.lenderId){
            return { financeCompanyId: p.lenderId!, valid: true, accepted: true, reason: null }
          }
        }

        if(this.vehicle!.monthlyPrices!.find(pr => pr.lenderId === p.lenderId && !pr.failed)){
            return { financeCompanyId: p.lenderId!, valid: true, reason: null }
        }

        const failedVehicle = this.vehicle!.monthlyPrices!.find(r => r.lenderId === p.lenderId && r.failed);
        
        if(failedVehicle){
            let reason = "Vehicle not valid";
            return { financeCompanyId: p.lenderId!, valid: false, reason: reason ? sanitiseVehicleFailReason(this.vehicle!, reason) : null, failReasons: failedVehicle.failReasons }
        }

        return { financeCompanyId: p.lenderId!, valid: true, reason: null}
      });
    } 

    return lenders;
  }
}

export type { ILoanApplication };
export { LoanApplication };